import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { MaskedLines, EASE } from "@/components/motion/Reveal";
import { CtaButton } from "@/components/brand/CtaButton";
import { LogoMark } from "@/components/brand/Logo";
import { DIVISIONS } from "@/content/company";

const Badge = () => (
  <div className="relative h-[72px] w-[72px] shrink-0" aria-hidden="true" data-testid="hero-badge">
    <svg viewBox="0 0 200 200" className="spin-slow h-full w-full">
      <defs><path id="circle" d="M100,100 m-76,0 a76,76 0 1,1 152,0 a76,76 0 1,1 -152,0" /></defs>
      <circle cx="100" cy="100" r="97" fill="none" stroke="#C9A54C" strokeOpacity=".45" />
      <text fill="#E9D08A" fontSize="16.5" letterSpacing="4" fontFamily="JetBrains Mono, monospace">
        <textPath href="#circle">LEARN · INNOVATE · TRANSFORM · </textPath>
      </text>
    </svg>
    <LogoMark className="absolute left-1/2 top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2" />
  </div>
);

const HeroVisual = ({ progress }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const imgY = useTransform(progress, [0, 1], ["0%", "22%"]);
  const frameY = useTransform(progress, [0, 1], [0, -60]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <motion.div style={{ y: frameY }} className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={onLeave}>
      <div aria-hidden="true" className="pointer-events-none absolute -inset-x-4 -bottom-4 -top-4 mx-auto max-w-[492px] rounded-t-[999px] rounded-b-[36px] border border-gold/25" />
      <motion.div
        initial={{ opacity: 0, clipPath: "inset(100% 0 0 0 round 999px 999px 28px 28px)" }}
        animate={{ opacity: 1, clipPath: "inset(0% 0 0 0 round 999px 999px 28px 28px)" }}
        transition={{ duration: 1.6, ease: EASE, delay: 0.3 }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative mx-auto h-[440px] w-full max-w-[460px] sm:h-[540px] lg:h-[620px]"
        data-testid="hero-visual"
      >
        <div className="absolute inset-0 overflow-hidden rounded-t-[999px] rounded-b-[28px] ring-1 ring-gold/30">
          <motion.img
            src="/images/hero-tech.webp"
            alt="African technology professional with a laptop in a modern data centre"
            style={{ y: imgY }}
            fetchPriority="high"
            className="absolute inset-0 h-[124%] w-full -translate-y-[10%] scale-105 object-cover object-[58%_50%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-plum/20 to-plum/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_15%,rgba(233,208,138,0.22),transparent_55%)] mix-blend-screen" />
        </div>
        <div style={{ transform: "translateZ(60px)" }} className="absolute bottom-6 left-[7%] right-[7%] flex items-center gap-4 rounded-2xl border border-white/15 bg-ink/75 p-4 backdrop-blur-md">
          <Badge />
          <div className="min-w-0">
            <p className="eyebrow truncate text-[10px] text-gold-light">VION Technology Limited</p>
            <p className="mt-1.5 font-display text-lg font-light leading-snug text-white sm:text-xl">Learn. Innovate. Transform.</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const HomeHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section ref={ref} className="grain relative overflow-hidden bg-ink pt-32 sm:pt-40" data-testid="home-hero">
      <div aria-hidden="true" className="pointer-events-none absolute -left-48 -top-24 h-[640px] w-[640px] rounded-full bg-plum-3/45 blur-[140px]" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 right-[-10%] h-[420px] w-[420px] rounded-full bg-gold/10 blur-[120px]" />
      <div className="shell relative z-[2] grid items-center gap-16 pb-10 sm:pb-20 lg:grid-cols-12 lg:gap-8 lg:pb-28">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="eyebrow text-gold" data-testid="hero-eyebrow">
            AI • Technology • Education • Global Opportunities
          </motion.p>
          <MaskedLines
            testid="hero-headline"
            lines={["Building Skills.", "Transforming", "Businesses.", "Creating Opportunities."]}
            className="mt-8 font-display text-[2.5rem] font-light leading-[1] tracking-[-0.03em] text-white sm:text-6xl lg:text-[4.5rem] xl:text-[5rem]"
            lineClassName={(i) => (i === 3 ? "italic text-gold-metal shimmer" : i === 1 ? "pl-[0.6em] sm:pl-[1.2em]" : "")}
          />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.75 }} className="mt-10 max-w-xl text-base leading-relaxed text-white/75 md:text-lg" data-testid="hero-subheadline">
            VION Tech helps people and organisations learn, adopt and benefit from AI and emerging technologies — and connects them to global education, career and relocation opportunities.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.9 }} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <CtaButton href="#ecosystem" target="_self" rel="" testid="hero-explore-services-btn" onClick={(e) => { e.preventDefault(); document.getElementById("ecosystem")?.scrollIntoView({ behavior: "smooth" }); }}>
              Explore Our Services
            </CtaButton>
            <CtaButton to="/contact" variant="outline" testid="hero-work-with-us-btn">Work With Us</CtaButton>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5">
          <HeroVisual progress={scrollYProgress} />
        </div>
      </div>
      <div className="relative z-[2] border-t border-white/10">
        <div className="shell grid grid-cols-1 sm:grid-cols-3">
          {DIVISIONS.map((d, i) => (
            <motion.div key={d.key} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 + i * 0.1, duration: 0.8 }}>
              <Link to={d.to} data-testid={`hero-division-link-${d.key}`} className={`group flex min-h-[64px] items-center justify-between gap-4 py-5 sm:px-6 ${i ? "border-t border-white/10 sm:border-l sm:border-t-0" : ""} sm:first:pl-0`}>
                <span className="flex items-baseline gap-3">
                  <span className="font-mono text-[11px] text-gold">{d.index}</span>
                  <span className="text-[15px] text-white/85 transition-colors group-hover:text-white">{d.name}</span>
                </span>
                <span className="h-px w-6 bg-white/30 transition-[width,background-color] duration-500 group-hover:w-12 group-hover:bg-gold" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
